/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { SelectField, SelectOption, typography2 } from '@coursera/cds-core';

import {
  ALL_LEARNERS_FILTER_LABEL,
  ALL_STAR_VALUES,
  COMPLETERS_FILTER_LABEL,
  LEARNER_FILTERS,
  MOST_HELPFUL_SORT_LABEL,
  MOST_RECENT_SORT_LABEL,
  SORT_OPTIONS,
  STAR_FILTER_LABEL,
  STAR_VALUES,
} from 'bundles/reviews/constants';

import _t from 'i18n!nls/reviews';

const styles = {
  row: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-200);
  `,
  // Chip-like select styling (from RoundedSelectField)
  dropdown: css`
    display: flex;
    background-color: transparent;
    flex-shrink: 0;

    .cds-input-root {
      border-radius: var(--cds-border-radius-400);
      margin-top: 0;
      height: 36px;
      width: fit-content;

      svg {
        height: 16px;
        width: 16px;
      }

      fieldset {
        border-color: var(--cds-color-neutral-stroke-primary-weak);
        border-radius: var(--cds-border-radius-400);
      }

      .cds-input-input {
        font: ${typography2.actionSecondary.font} !important;
        letter-spacing: ${typography2.actionSecondary.letterSpacing} !important;
        align-content: center;

        :hover,
        :active {
          color: var(--cds-color-interactive-primary-hover) !important;
          border-radius: var(--cds-border-radius-400);
        }
      }
    }

    .cds-select-focusVisible::before {
      top: -2px !important;
      left: -2px !important;
      right: -2px !important;
      bottom: -2px !important;
      border-radius: var(--cds-border-radius-400) !important;
    }
  `,
};

type ReviewsTableToolbarProps = {
  productCompleted: boolean | undefined;
  ratingValues: number[];
  sortByHelpfulVotes: boolean;
  onLearnerChange: (productCompleted: boolean) => void;
  onStarChange: (ratingValues: number[]) => void;
  onSortChange: (sortByHelpfulVotes: boolean) => void;
};

export function ReviewsTableToolbar({
  productCompleted,
  ratingValues,
  sortByHelpfulVotes,
  onLearnerChange,
  onStarChange,
  onSortChange,
}: ReviewsTableToolbarProps) {
  const learnerValue = productCompleted === true ? LEARNER_FILTERS.COMPLETERS : LEARNER_FILTERS.ALL_LEARNERS;
  const starValue =
    ratingValues.length === ALL_STAR_VALUES.length
      ? LEARNER_FILTERS.ALL_LEARNERS
      : ratingValues.length === 1
      ? String(ratingValues[0])
      : LEARNER_FILTERS.ALL_LEARNERS;
  const sortValue = sortByHelpfulVotes ? SORT_OPTIONS.MOST_HELPFUL : SORT_OPTIONS.MOST_RECENT;

  return (
    <div css={styles.row} data-testid="reviews-table-toolbar" data-e2e="reviews-table-toolbar">
      <SelectField
        value={learnerValue}
        onChange={(e) => {
          const v = (e.target as HTMLSelectElement).value;
          onLearnerChange(v === LEARNER_FILTERS.COMPLETERS);
        }}
        css={styles.dropdown}
        aria-label={_t('Learner type')}
        data-e2e="reviews-table-toolbar-learners"
      >
        <SelectOption value={LEARNER_FILTERS.ALL_LEARNERS} data-e2e="reviews-table-toolbar-learners-all">
          {ALL_LEARNERS_FILTER_LABEL()}
        </SelectOption>
        <SelectOption value={LEARNER_FILTERS.COMPLETERS} data-e2e="reviews-table-toolbar-learners-completers">
          {COMPLETERS_FILTER_LABEL()}
        </SelectOption>
      </SelectField>

      <SelectField
        value={starValue}
        onChange={(e) => {
          const v = (e.target as HTMLSelectElement).value;
          if (v === LEARNER_FILTERS.ALL_LEARNERS) {
            onStarChange(ALL_STAR_VALUES);
          } else {
            const num = parseInt(v, 10);
            if (!Number.isNaN(num)) onStarChange([num]);
          }
        }}
        css={styles.dropdown}
        aria-label={_t('Star rating')}
        data-e2e="reviews-table-toolbar-stars"
      >
        {[
          <SelectOption key={LEARNER_FILTERS.ALL_LEARNERS} value={LEARNER_FILTERS.ALL_LEARNERS}>
            {STAR_FILTER_LABEL()}
          </SelectOption>,
          ...STAR_VALUES.filter((s) => s.value.length === 1).map((s) => (
            <SelectOption
              key={s.dataE2eValue}
              value={String(s.value[0])}
              data-e2e={`reviews-table-toolbar-stars-${s.dataE2eValue}`}
            >
              {s.label}
            </SelectOption>
          )),
        ]}
      </SelectField>

      <SelectField
        value={sortValue}
        onChange={(e) => {
          const v = (e.target as HTMLSelectElement).value;
          onSortChange(v === SORT_OPTIONS.MOST_HELPFUL);
        }}
        css={styles.dropdown}
        aria-label={_t('Sort order')}
        data-e2e="reviews-table-toolbar-sort"
      >
        <SelectOption value={SORT_OPTIONS.MOST_HELPFUL} data-e2e="reviews-table-toolbar-sort-helpful">
          {MOST_HELPFUL_SORT_LABEL()}
        </SelectOption>
        <SelectOption value={SORT_OPTIONS.MOST_RECENT} data-e2e="reviews-table-toolbar-sort-recent">
          {MOST_RECENT_SORT_LABEL()}
        </SelectOption>
      </SelectField>
    </div>
  );
}

export default ReviewsTableToolbar;
